import {
  errorResponseSchema,
  successResponseSchema,
  type ErrorResponse,
  type Paginated,
} from '@nest-react-monorepo/contracts';
import { z } from 'zod';
import { ApiRequestError, ApiTimeoutError, UNKNOWN_REQUEST_ID } from './api-error.js';
import { chosenBoxHeader } from './chosen-box.js';
import { currentLocale } from './i18n.js';
import { captureError } from './monitoring.js';

export { ApiRequestError, ApiTimeoutError, UNKNOWN_REQUEST_ID } from './api-error.js';

/**
 * The exchange app talks to the Currency API through its own origin by
 * default (`/api/v1`, proxied by the Vite dev and preview servers), so the
 * dashboard's shared `VITE_API_URL` and CORS list do not have to change. A
 * deployment that serves the API elsewhere sets `VITE_EXCHANGE_API_URL`.
 */
const BASE_URL = import.meta.env['VITE_EXCHANGE_API_URL'] ?? '/api/v1';

/**
 * currency.md section 4.1: tells a shared API route to prefer this app's
 * `fx_exchange_session` cookie when the browser also holds the dashboard's.
 */
export const APP_HEADER = { 'x-fx-app': 'exchange' } as const;

/**
 * Rule E8: `fetch` has no deadline of its own. A hung mutation must never
 * trap the user with no way out — the worst case in this application is a
 * posted currency exchange nobody can back out of. This bounds every
 * request so it always eventually settles, one way or another.
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 20_000;

interface RequestOptions<TResponse> {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Response `data` schema. Parsing here means a backend change fails loudly. */
  schema?: z.ZodType<TResponse>;
  signal?: AbortSignal;
  /** Rule E8. Overridable for tests and long-running exceptions. */
  timeoutMs?: number;
  /** Extra request headers, e.g. `Idempotency-Key`. */
  headers?: Record<string, string>;
}

function isFormData(body: unknown): body is FormData {
  return typeof FormData !== 'undefined' && body instanceof FormData;
}

async function parseError(response: Response): Promise<ErrorResponse> {
  try {
    const json: unknown = await response.json();
    const parsed = errorResponseSchema.safeParse(json);
    if (parsed.success) return parsed.data;
  } catch {
    // Falls through to the synthetic envelope below.
  }

  return {
    success: false,
    statusCode: response.status,
    error: {
      code: response.status >= 500 ? 'INTERNAL_ERROR' : 'UNPROCESSABLE',
      message: response.statusText || 'Request failed',
    },
    requestId: response.headers.get('x-request-id') ?? UNKNOWN_REQUEST_ID,
    timestamp: new Date().toISOString(),
    locale: currentLocale(),
    path: response.url,
  };
}

export type { Paginated };

/**
 * The one place that calls `fetch` for this application. Session state lives
 * entirely in the `cur_session` cookie Unified Access's launch redemption
 * sets (`credentials: 'include'`) — there is no bearer token, no local
 * refresh flow, and no local login. A bounded deadline keeps the UI from
 * getting stuck on a hung request indefinitely.
 */
export async function apiRequest<TResponse = unknown>(
  path: string,
  options: RequestOptions<TResponse> = {},
): Promise<TResponse> {
  const {
    method = 'GET',
    body,
    schema,
    signal,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    headers,
  } = options;

  // A caller that supplies its own `signal` (e.g. a TanStack Query `GET`)
  // keeps full, unwrapped ownership of it; that exact object still reaches
  // `fetch` unchanged, so a caller-cancelled request stays distinguishable
  // from this deadline firing.
  let timedOut = false;
  const ownDeadline = signal ? undefined : new AbortController();
  const requestSignal = signal ?? ownDeadline!.signal;
  const timer =
    timeoutMs > 0 && ownDeadline
      ? setTimeout(() => {
          timedOut = true;
          ownDeadline.abort();
        }, timeoutMs)
      : undefined;

  try {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method,
        credentials: 'include',
        headers: {
          'Accept-Language': currentLocale(),
          ...APP_HEADER,
          ...chosenBoxHeader(),
          // A multipart body sets its own boundary header.
          ...(body === undefined || isFormData(body) ? {} : { 'Content-Type': 'application/json' }),
          ...headers,
        },
        ...(body === undefined ? {} : { body: isFormData(body) ? body : JSON.stringify(body) }),
        signal: requestSignal,
      });
    } catch (error) {
      throw timedOut ? new ApiTimeoutError() : error;
    }

    if (!response.ok) {
      const payload = await parseError(response);
      const failure = new ApiRequestError(response.status, payload);
      // captureError itself ignores 4xx; a rejected form is not an incident.
      captureError(failure, { path });
      throw failure;
    }

    if (response.status === 204) return undefined as TResponse;

    const json: unknown = await response.json();
    const envelope = successResponseSchema(schema ?? z.unknown()).parse(json);
    // The success envelope carries `meta` as a SIBLING of `data`
    // (packages/contracts/src/common/envelope.ts), not nested inside it. A
    // list endpoint's caller expects `{ data, meta }` back; a single-record
    // endpoint has `meta: null` and returns `data` bare. Dropping `meta`
    // here would silently starve every paginated list of its own response
    // shape — callers would still "work" but every list would render empty
    // regardless of how much data the server actually returned.
    if (envelope.meta === null || envelope.meta === undefined) return envelope.data as TResponse;
    return { data: envelope.data, meta: envelope.meta } as TResponse;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * A binary GET (an image) through the same session, deadline and error
 * envelope as `apiRequest`.
 */
export async function apiBlob(
  path: string,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<Blob> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    let response: Response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        credentials: 'include',
        headers: { 'Accept-Language': currentLocale(), ...APP_HEADER },
        signal: controller.signal,
      });
    } catch (error) {
      throw timedOut ? new ApiTimeoutError() : error;
    }
    if (!response.ok) {
      const failure = new ApiRequestError(response.status, await parseError(response));
      captureError(failure, { path });
      throw failure;
    }
    return await response.blob();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * A fire-and-forget POST that survives the page unloading (`keepalive`), for
 * best-effort clean-up such as freeing the tablet. Errors are ignored.
 */
export function apiKeepalivePost(path: string): void {
  try {
    void fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
      headers: { 'Accept-Language': currentLocale(), ...APP_HEADER },
    }).catch(() => undefined);
  } catch {
    // Nothing to do: the task expires on the tablet anyway.
  }
}
